import { useState, useEffect, useRef, useCallback } from 'react';
import { Modal, TextField, Banner, DescriptionList, Box, Divider, InlineGrid, BlockStack, ButtonGroup, Button } from '@shopify/polaris';
import { formatToCurrency, getCurrenctDatetimeYYYMMDD, getRequest, putRequest } from '../../../shared/components/functions';
import { performTracking } from '../../../analytics/segment';
import IncasserenSelect from '../../../shared/components/incasserenSelect';
import EpcPaymentQrCode from './EpcPaymentQrCode';
import BankPaymentPanel from './BankPaymentPanel';
import { labelFor } from '../../../shared/components/approvals/approvalStatus';
import { useAuthStore } from '../../../context/useStore';

function getInitialPaymentState(collectionInvoice, fullInvoiceCollection) {
  return {
    bedragopen: fullInvoiceCollection ? 0 : Math.round(collectionInvoice.bedragopen * 100) / 100,
    faktuurinvolgnummer: collectionInvoice.faktuurinvolgnummer,
    memo: collectionInvoice.memo || '',
    status: fullInvoiceCollection ? 1 : collectionInvoice.status,
    partiallyPaid: 0,
    datumbetalingout: fullInvoiceCollection ? getCurrenctDatetimeYYYMMDD() : null,
    Incasseren: fullInvoiceCollection ? 'BELFIUS' : null
  };
}

function PaymentFormContent({ collectionInvoice, fullInvoiceCollection, onPaymentStateChange }) {
  const { bedragopen, faktuurinvolgnummer, memo, status } = collectionInvoice;
  const [state, setState] = useState(() => getInitialPaymentState(collectionInvoice, fullInvoiceCollection));

  useEffect(() => {
    const nextState = getInitialPaymentState({ bedragopen, faktuurinvolgnummer, memo, status }, fullInvoiceCollection);
    setState(nextState);
    onPaymentStateChange(nextState);
  }, [bedragopen, faktuurinvolgnummer, memo, status, fullInvoiceCollection, onPaymentStateChange]);

  const handleChange = (value, id) => {
    setState((state) => {
      const nextState = { ...state, [id]: value };

      if (id === 'partiallyPaid') {
        const partiallyPaid = parseFloat(String(value).replace(',', '.'));
        nextState.bedragopen = Number.isNaN(partiallyPaid) ? Math.round(collectionInvoice.bedragopen * 100) / 100 : Math.round((collectionInvoice.bedragopen - partiallyPaid) * 100) / 100;
      }

      onPaymentStateChange(nextState);
      return nextState;
    });
  };

  return (
    <BlockStack gap="400">
      {fullInvoiceCollection ? (
        <Banner tone="info">The invoice status will be automatically set to paid. And the open amount to 0.</Banner>
      ) : (
        <Banner tone="warning">The invoice status will not be set to paid. Please set the remaining open value yourself.</Banner>
      )}
      {collectionInvoice.ApprovalStatus && collectionInvoice.ApprovalStatus !== 'APPROVED' ? (
        <Banner tone="warning" title="Not fully approved">
          This invoice is {labelFor(collectionInvoice.ApprovalStatus).toLowerCase()}
          {collectionInvoice.assignee_afkorting ? ` (assigned to ${collectionInvoice.assignee_afkorting})` : ''}. Registering the payment stays possible, but it is logged on the invoice timeline.
        </Banner>
      ) : null}

      <DescriptionList
        items={[
          {
            term: 'Invoice number',
            description: `#${collectionInvoice.FactuurInNummer}`
          },
          {
            term: 'Invoice reference',
            description: collectionInvoice.faktuurref
          },
          {
            term: 'Supplier',
            description: collectionInvoice.supplier_name
          },
          {
            term: 'Total value',
            description: formatToCurrency(collectionInvoice.Bedragfaktuur)
          }
        ]}
      />

      <Divider />

      {fullInvoiceCollection && <TextField id="datumbetalingout" type="date" label="Payment date" value={String(state.datumbetalingout).substring(0, 10)} onChange={handleChange} />}

      {!fullInvoiceCollection && (
        <TextField prefix="€" id="partiallyPaid" disabled={fullInvoiceCollection} type="currency" label="AMOUNT THAT IS PARTIALLY PAID" value={String(state.partiallyPaid)} onChange={handleChange} />
      )}

      <TextField id="bedragopen" disabled type="number" prefix="€" label="Open amount" value={String(state.bedragopen)} onChange={handleChange} />

      {fullInvoiceCollection && <IncasserenSelect id="Incasseren" value={state.Incasseren} handleChange={handleChange} />}

      <TextField multiline={4} id="memo" label="Note" value={state.memo} onChange={handleChange} />
    </BlockStack>
  );
}

/*
  Payment modal of a supplier invoice. Two ways to settle it, side by side:
    left   the classic manual registration ("Mark as fully / partially paid") — unchanged
    right  pay through the bank: a Ponto payment intent (users with banking access), or the EPC QR code
           to scan with a banking app (Peppol invoices). A bank payment does NOT mark the invoice as paid:
           it stays open until the bank's debit is matched by the banking integration.
*/
export default function InvoiceInCollection({ modalOpen, setModalOpen, fullInvoiceCollection, collectionInvoice, fetchData }) {
  const { user } = useAuthStore();
  const paymentStateRef = useRef(getInitialPaymentState(collectionInvoice, fullInvoiceCollection));
  const [loading, setLoading] = useState(false);
  const [peppolPayload, setPeppolPayload] = useState(null);
  const [bankAvailable, setBankAvailable] = useState(null); // null = not known yet, false = panel reported it cannot be used
  const [bankSent, setBankSent] = useState(null); // { intent, signingLink } after a payment was sent to the bank
  const [rightView, setRightView] = useState('bank'); // 'bank' | 'qr'

  const hasPeppolData = !!peppolPayload;
  const bankFlowPossible = Boolean(user?.bankingAccess) && !collectionInvoice.status && Boolean(collectionInvoice.faktuurinvolgnummer);
  const showBank = bankFlowPossible && bankAvailable !== false;
  const showQr = fullInvoiceCollection && hasPeppolData;

  const handlePaymentStateChange = useCallback((nextState) => {
    paymentStateRef.current = nextState;
  }, []);
  const handleBankAvailability = useCallback((available) => setBankAvailable(available), []);
  const handleBankSent = useCallback(
    (result) => {
      setBankSent(result);
      setRightView('bank');
      fetchData();
    },
    [fetchData]
  );

  useEffect(() => {
    if (!modalOpen) return;
    setBankSent(null);
    setBankAvailable(null);
    setRightView('bank');
  }, [modalOpen, collectionInvoice.faktuurinvolgnummer]);

  useEffect(() => {
    let isActive = true;

    setPeppolPayload(collectionInvoice.PeppolJSON || null);

    if (!modalOpen || !fullInvoiceCollection || collectionInvoice.PeppolJSON || !collectionInvoice.faktuurinvolgnummer) {
      return () => {
        isActive = false;
      };
    }

    async function fetchPeppolPayload() {
      const invoice = await getRequest(`/api/invoices_in/${collectionInvoice.faktuurinvolgnummer}`);
      const invoiceRecord = Array.isArray(invoice) ? invoice[0] : invoice;
      if (isActive) {
        setPeppolPayload(invoiceRecord?.PeppolJSON || null);
      }
    }

    fetchPeppolPayload();

    return () => {
      isActive = false;
    };
  }, [collectionInvoice.PeppolJSON, collectionInvoice.faktuurinvolgnummer, fullInvoiceCollection, modalOpen]);

  const closeModal = () => setModalOpen(false);

  const collectInvoice = async () => {
    const paymentState = paymentStateRef.current;
    setLoading(true);
    await putRequest(`/api/invoices_in/${paymentState.faktuurinvolgnummer}`, paymentState);
    setModalOpen(false);
    setLoading(false);
    await fetchData();
    if (fullInvoiceCollection) {
      performTracking('payment_collected_invoice_in_full', paymentState);
    } else {
      performTracking('payment_collected_invoice_in_partial', paymentState);
    }
  };

  const qrCodeContent = (
    <EpcPaymentQrCode peppolPayload={peppolPayload} invoiceNumber={collectionInvoice.FactuurInNummer} openAmount={collectionInvoice.bedragopen || collectionInvoice.Bedragfaktuur} />
  );

  const bankPanel = bankFlowPossible ? (
    <BankPaymentPanel invoiceId={collectionInvoice.faktuurinvolgnummer} open={modalOpen} onAvailability={handleBankAvailability} onSent={handleBankSent} onIntentChange={fetchData} />
  ) : null;

  const rightColumn =
    showBank || showQr ? (
      <BlockStack gap="300">
        {showBank && showQr && !bankSent ? (
          <ButtonGroup variant="segmented">
            <Button pressed={rightView === 'bank'} onClick={() => setRightView('bank')}>
              Send to bank
            </Button>
            <Button pressed={rightView === 'qr'} onClick={() => setRightView('qr')}>
              Scan QR code
            </Button>
          </ButtonGroup>
        ) : null}
        {/* the bank panel stays mounted while the QR code is shown, so a sent payment is not lost by switching */}
        {bankPanel ? <div style={{ display: showBank && (rightView === 'bank' || !showQr) ? 'block' : 'none' }}>{bankPanel}</div> : null}
        {showQr && (rightView === 'qr' || !showBank) ? qrCodeContent : null}
      </BlockStack>
    ) : null;

  const manualForm = <PaymentFormContent collectionInvoice={collectionInvoice} fullInvoiceCollection={fullInvoiceCollection} onPaymentStateChange={handlePaymentStateChange} />;

  return (
    <Modal
      open={modalOpen}
      onClose={closeModal}
      title={`Payment invoice #${collectionInvoice.FactuurInNummer}`}
      primaryAction={
        bankSent
          ? { content: 'Done', onAction: closeModal }
          : {
              content: fullInvoiceCollection ? 'Mark as fully paid' : 'Mark as partially paid',
              onAction: () => collectInvoice(),
              loading: loading
            }
      }
      secondaryActions={bankSent ? [] : [{ content: 'Cancel', onAction: closeModal }]}
      size="large"
    >
      <Modal.Section>
        {/*
          Keep the tree shape stable: the bank panel must never move to another position, or React remounts
          it and the "sent to the bank" state is lost. After sending, the manual form is simply left out —
          nothing is left to register by hand, the invoice closes itself once the debit is matched.
        */}
        <InlineGrid columns={{ xs: 1, md: rightColumn && !bankSent ? 2 : 1 }} gap="600">
          {bankSent ? null : <Box>{manualForm}</Box>}
          {rightColumn ? <Box>{rightColumn}</Box> : null}
        </InlineGrid>
      </Modal.Section>
    </Modal>
  );
}
